import { Link, Pagination, Pill, Spinner, Table as TableComponent } from '@/components/Elements';
import { Receipt } from '@/components/Elements/Receipt';
import { CurrencyValue } from '@/features/countries';
import { useAuth } from '@/lib/auth';
import { StatusColor } from '@/types';

import { useReviewedAdditionalCompensations } from '../../api/getReviewedAdditionalCompensations';
import { CancelAdditionalCompensation } from '../CancelAdditionalCompensation';

import { DownloadReviewedAdditionalCompensationsXLS } from './DownloadReviewedAdditionalCompensationsXLS';

type Compensation = {
  id: number;
  workerId: number;
  avatar: string;
  fullName: string;
  description: string;
  type: string;
  amount: string;
  createdAt: string;
  receipt?: string;
  status: string;
  currencyCode: string;
  requesterType: string | null;
};

type TStatusColors<Color = StatusColor> = {
  accepted: Color;
  canceled: Color;
  paid: Color;
  processed: Color;
  rejected: Color;
};

const Status = ({
  status,
  id,
  requesterType,
}: Pick<Compensation, 'status' | 'id' | 'requesterType'>) => {
  const canCancel = status === 'accepted';
  const isclient = requesterType === 'client';

  if (canCancel && isclient) return <CancelAdditionalCompensation id={id} />;

  const statusTypes: TStatusColors = {
    accepted: StatusColor.Green,
    canceled: StatusColor.Gray,
    paid: StatusColor.Gray,
    processed: StatusColor.Gray,
    rejected: StatusColor.Red,
  };
  return (
    <Pill
      className="text-xs font-semibold uppercase"
      color={statusTypes[status as keyof TStatusColors]}
    >
      {status}
    </Pill>
  );
};

export const Table = () => {
  const { user } = useAuth();

  const reviewedAdditionalCompensationsQuery = useReviewedAdditionalCompensations({
    config: {
      keepPreviousData: true,
    },
  });

  if (reviewedAdditionalCompensationsQuery.isLoading) {
    return (
      <div className="flex h-48 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!reviewedAdditionalCompensationsQuery.data) return null;

  const { data, links, meta } = reviewedAdditionalCompensationsQuery.data;

  const compensations: Compensation[] = data.map((compensation) => ({
    workerId: compensation.workerId,
    id: compensation.id,
    avatar: compensation.avatar,
    fullName: compensation.fullName,
    description: compensation.description,
    type: compensation.type,
    amount: compensation.amount,
    createdAt: compensation.createdAt,
    status: compensation.status,
    receipt: compensation.receipt,
    currencyCode: compensation.currencyCode,
    requesterType: compensation.requesterType,
  }));

  return (
    <TableComponent<Compensation>
      data={compensations}
      emptyState="No reimbursements found"
      isFetching={
        reviewedAdditionalCompensationsQuery.isFetching &&
        reviewedAdditionalCompensationsQuery.isPreviousData
      }
      columns={[
        {
          title: 'Status',
          field: 'status',
          Cell: ({ entry: { status, id, requesterType } }) => (
            <Status status={status} id={id} requesterType={requesterType} />
          ),
        },
        {
          title: 'Worker',
          field: 'fullName',
          Cell: ({ entry: { avatar, fullName, workerId } }) => (
            <div className="flex items-center gap-x-2">
              <img className="mr-1 h-8 w-8 rounded-full" src={avatar} alt="user avatar" />
              <Link to={`/app/team/worker/${workerId}`}>
                <span className="font-medium text-ebony-clay-900 underline underline-offset-2 hover:text-ebony-clay-700">
                  {fullName}
                </span>
              </Link>
            </div>
          ),
        },
        {
          title: 'Type',
          field: 'type',
          Cell: ({ entry: { type } }) => <p className="capitalize-first">{type}</p>,
        },
        {
          title: 'Date',
          field: 'createdAt',
        },
        {
          title: 'Amount',
          field: 'amount',
          Cell: ({ entry: { amount, currencyCode } }) => (
            <CurrencyValue currencyCode={currencyCode} value={amount} />
          ),
          isSortable: false,
        },
        {
          title: 'Receipt',
          field: 'receipt',
          isSortable: false,
          Cell: ({ entry: { receipt } }) => <Receipt url={receipt} />,
        },
        {
          title: 'Description',
          field: 'description',
          isSortable: false,
        },
      ]}
      downloadXLS={
        <DownloadReviewedAdditionalCompensationsXLS
          companyName={user?.data.company.name || ''}
          title="Additional Compensation"
        />
      }
      pagination={
        <Pagination
          links={links}
          meta={meta}
          path="/additional-compensation/reviewed"
          isFetching={
            reviewedAdditionalCompensationsQuery.isFetching &&
            reviewedAdditionalCompensationsQuery.isPreviousData
          }
        />
      }
    />
  );
};
